/**
 * RoleEditDialog - shared "rename / edit" dialog for both internal
 * organisation roles (RolesTab) and external partner roles
 * (ExternalRolesTab).
 *
 * Both role types expose a partial PUT that accepts ``name`` and
 * ``description`` (permissions are left untouched when they are omitted from
 * the payload), so this dialog only ever sends those two fields. Permissions
 * continue to be edited on their own surfaces (the permission editor modal /
 * the expandable matrix).
 */

import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { RoleAttributesSection } from './role-attributes-section'

export interface RoleEditValues {
  name: string
  description: string
}

interface RoleEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Dialog heading, e.g. "Edit Role" / "Edit External Role". */
  title: string
  initialName: string
  initialDescription: string
  saving: boolean
  onSave: (values: RoleEditValues) => Promise<void>
  /**
   * When set (internal roles only), render the role's custom-attributes editor
   * below the name/description form. External partner roles carry their own
   * attribute model, so they leave this unset.
   */
  roleUuid?: string
}

const FORM_ID = 'edit-role-form'

export function RoleEditDialog({
  open,
  onOpenChange,
  title,
  initialName,
  initialDescription,
  saving,
  onSave,
  roleUuid,
}: RoleEditDialogProps) {
  const { t } = useTranslation()
  // Static field definitions (empty defaults). The dialog is re-seeded with
  // the selected role's current values via resetForm whenever it opens, so
  // the same form instance is reused across different roles.
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: t('roles.roleName', 'Role name'),
        placeholder: t('roles.roleEditNamePlaceholder', 'e.g. Dealer, Service Partner'),
        value: '',
        autoFocus: true,
        validationSchema: z
          .string()
          .min(1, t('roles.roleNameRequired', 'Role name is required')),
      },
      {
        type: 'text',
        name: 'description',
        label: t('common.description', 'Description'),
        placeholder: t('roles.optionalDescription', 'Optional description'),
        value: '',
      },
    ],
    [t],
  )

  const { Form: EditRoleForm, ref: editRoleFormRef } = useForm(
    {
      name: FORM_ID,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as RoleEditValues
      await onSave({
        name: String(values.name ?? '').trim(),
        description: String(values.description ?? '').trim(),
      })
    },
    [fields, onSave],
  )

  // Seed the form with the role's current values each time the dialog opens.
  useEffect(() => {
    if (open) {
      editRoleFormRef.current?.resetForm({
        values: { name: initialName, description: initialDescription },
      })
    }
  }, [open, initialName, initialDescription, editRoleFormRef])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Only override the width (base DialogContent already supplies the p-6
          body padding, gap-4 spacing, rounding and height cap). Wider when the
          attributes editor is shown (internal roles); external roles stay
          compact. */}
      <DialogContent className={roleUuid ? 'sm:max-w-lg' : 'sm:max-w-md'}>
        <DialogHeader>
          <DialogTitle className="text-[15px] text-[var(--ink)]">
            {title}
          </DialogTitle>
        </DialogHeader>

        <EditRoleForm className="space-y-4" />

        {roleUuid && (
          <RoleAttributesSection roleUuid={roleUuid} roleName={initialName} />
        )}

        <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={saving}
            data-testid="role-edit-dialog-submit"
            className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />}
            {t('common.saveChanges', 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
